import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import Api from '../../../../../component/Api';

export const useMarkSubmission = (studentId, studentData, onBack, onSubmitSuccess, onRefresh, onStatusUpdate) => {
    const { http } = useMemo(() => Api(), []);
    const [cca, setCcaMark] = useState('');
    const [ese, setEseMark] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const originalValues = useRef({ cca: '', ese: '' });

    useEffect(() => {
        if (studentData) {
            const ccaValue = studentData.cca !== null && studentData.cca !== undefined ? studentData.cca.toString() : '';
            const eseValue = studentData.ese !== null && studentData.ese !== undefined ? studentData.ese.toString() : '';
            setCcaMark(ccaValue);
            setEseMark(eseValue);
            originalValues.current = { cca: ccaValue, ese: eseValue };
        }
    }, [studentData]);

    const validateMarks = useCallback(() => {
        const errors = [];
        if (cca) {
            const ccaValue = parseFloat(cca);
            const ccaMax = parseFloat(studentData.cca_max) || 100;
            if (isNaN(ccaValue)) errors.push('CCA mark must be a valid number');
            else if (ccaValue < 0) errors.push('CCA mark cannot be negative');
            else if (ccaValue > ccaMax) errors.push(`CCA mark cannot exceed ${ccaMax}`);
        }
        if (ese) {
            const eseValue = parseFloat(ese);
            const eseMax = parseFloat(studentData.ese_max) || 100;
            if (isNaN(eseValue)) errors.push('ESE mark must be a valid number');
            else if (eseValue < 0) errors.push('ESE mark cannot be negative');
            else if (eseValue > eseMax) errors.push(`ESE mark cannot exceed ${eseMax}`);
        }
        if (!cca || !ese) errors.push('Please enter mark');
        return errors;
    }, [cca, ese, studentData]);

    const handleSubmit = useCallback(async () => {
        const validationErrors = validateMarks();
        if (validationErrors.length > 0) {
            Swal.fire({ title: 'Validation Error', html: validationErrors.join('<br>'), icon: 'error', confirmButtonColor: '#581f37' });
            return;
        }

        const result = await Swal.fire({
            title: 'Confirm Submission',
            html: `
                <div class="text-start">
                    <p class="mb-2"><strong>Student:</strong> ${studentData.student}</p>
                    <p class="mb-2"><strong>Course:</strong> ${studentData.course}</p>
                    ${cca ? `<p class="mb-2"><strong>CCA Mark:</strong> ${cca} / ${studentData.cca_max || 'N/A'}</p>` : ''}
                    ${ese ? `<p class="mb-2"><strong>ESE Mark:</strong> ${ese} / ${studentData.ese_max || 'N/A'}</p>` : ''}
                    <hr>
                    <p class="text-muted">Are you sure you want to save these marks?</p>
                </div>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#581f37',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, Save',
            cancelButtonText: 'Cancel',
            width: '500px',
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !Swal.isLoading(),
            preConfirm: async () => {
                try {
                    setIsSubmitting(true);
                    const payload = {
                        student_id: studentId ? parseInt(studentId, 10) : null,
                        cca_mark: cca ? parseFloat(cca) : null,
                        ese_mark: ese ? parseFloat(ese) : null,
                    };
                    const response = await http.post('/is/markSubmit', payload);
                    onStatusUpdate?.('draft');
                    return response;
                } catch (error) {
                    Swal.showValidationMessage(error?.response?.data?.message || 'Failed to save marks. Please try again.');
                } finally {
                    setIsSubmitting(false);
                }
            },
        });

        if (!result.isConfirmed) return;

        originalValues.current = { cca, ese };

        await Swal.fire({
            title: 'Saved!',
            html: `
                <div style="display:flex; flex-direction:column; align-items:center; gap:8px;">
                    <p style="margin:0;">Marks have been saved successfully.</p>
                    <p style="margin:0; font-size:0.9em; color:#6c757d;">
                        ${cca ? `CCA: <strong>${cca}</strong>` : ''}
                        ${cca && ese ? '&nbsp;|&nbsp;' : ''}
                        ${ese ? `ESE: <strong>${ese}</strong>` : ''}
                    </p>
                </div>`,
            icon: 'success',
            confirmButtonColor: '#581f37',
            confirmButtonText: 'Done',
        });

        onSubmitSuccess?.();
        //eslint-disable-next-line
    }, [validateMarks, studentData, cca, ese, studentId, http, onSubmitSuccess, onStatusUpdate]);

    const handleCancel = useCallback(() => {
        const hasChanges = cca !== originalValues.current.cca || ese !== originalValues.current.ese;
        if (hasChanges) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure you want to go back?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Yes, Discard',
                cancelButtonText: 'Stay',
            }).then(result => { if (result.isConfirmed) onBack(); });
        } else {
            onBack();
        }
    }, [cca, ese, onBack]);

    const handleCcaChange = useCallback((e) => {
        const value = e.target.value;
        if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) setCcaMark(value);
    }, []);

    const handleEseChange = useCallback((e) => {
        const value = e.target.value;
        if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) setEseMark(value);
    }, []);

    return { cca, ese, isSubmitting, handleSubmit, handleCancel, handleCcaChange, handleEseChange };
};